import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface CheckboxProps {
  name: string;
  value: boolean;
  onChange: (checked: boolean, name: string) => void;
  children: React.ReactNode;
}

function Checkbox({ name, value, onChange, children }: CheckboxProps) {
  return (
    <div className="py-1 flex items-center">
      <input
        type="checkbox"
        id={`${name}-checkbox`}
        checked={value}
        onChange={() => onChange(!value, name)}
        className="h-4 w-4"
      />
      <label htmlFor={`${name}-checkbox`} className="ml-1 capitalize text-xs">
        {children}
      </label>
    </div>
  );
}

interface CheckboxListProps {
  label: string;
  isRequired: boolean;
  value: string;
  options: Array<string | { label: string; value: string }>;
  onTextChange: (value: string) => void;
  errorText?: string;
  wrapperClassName?: string;
  labelTextClassName?: string;
}

const normalize = (str: string): string[] =>
  str
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .sort();

const CheckboxList: React.FC<CheckboxListProps> = ({
  label,
  isRequired,
  value,
  options,
  onTextChange,
  errorText = '',
  wrapperClassName = '',
  labelTextClassName,
}) => {
  const [selected, setSelected] = useState<string[]>(() => normalize(value));

  useEffect(() => {
    setSelected(normalize(value));
  }, [value]);

  // Most options are plain strings (label === value); supplier-branches/supplier-sales-reps
  // fields configured to save an ERP ID pass {label, value} pairs instead — see
  // resolveFieldOptions/resolveDropdownFieldOptions in conditionalOptionUtils.ts.
  const normalizedOptions = options.map((option) =>
    typeof option === 'string' ? { label: option, value: option } : option
  );

  const handleSelect = (checked: boolean, name: string) => {
    const next = checked ? [...selected, name] : selected.filter((item) => item !== name);

    const normalized = [...new Set(next)].sort();

    setSelected(normalized);
    onTextChange(normalized.join(', '));
  };

  return (
    <div className={`w-full ${wrapperClassName}`}>
      {(label || isRequired) && (
        <p className={twMerge('text-xs mb-1 text-left font-medium', labelTextClassName)}>
          {isRequired && <span className="text-red-500">*</span>}
          {label}
        </p>
      )}

      {normalizedOptions.map((option) => (
        <Checkbox
          key={option.value}
          name={option.value}
          value={selected.includes(option.value)}
          onChange={handleSelect}>
          {option.label}
        </Checkbox>
      ))}

      {errorText && <p className="text-2xs mt-0.5 text-red-500">{errorText}</p>}
    </div>
  );
};

export default CheckboxList;
